import React, {
  Suspense,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
  isValidElement,
  cloneElement,
  Children,
  useRef,
} from 'react'
import { scheduleOnRN } from 'react-native-worklets'
import {
  Easing,
  ReduceMotion,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'
import { View } from 'react-native'
import { Portal } from '@gorhom/portal'
import cn from '@ui/utils/cn'

import ModalContent from './ModalContent'
import { ModalContext } from './useModalContext'
import { ModalProps } from './types'

const modalTimingConfig = {
  duration: 500,
  easing: Easing.out(Easing.bezierFn(1, 0, 0.5, 0.25)),
  reduceMotion: ReduceMotion.Never,
}

export default function Modal({
  enabled,
  onClose,
  backdropClose,
  children,
  persistent,
  contentClassName,
  hostName,
  extraGestures,
  backdropContent,
  intensity,
  beforeClose,
}: ModalProps) {
  const animation = useSharedValue(0)
  const [visible, setVisible] = useState(enabled)
  // const [render, setRender] = useState(enabled)
  const isClosing = useSharedValue(false)

  const wasVisible = useRef(visible)

  const unMountContent = () => {
    setVisible(false)
    if (onClose) {
      scheduleOnRN(onClose)
    }
  }

  const show = () => {
    setVisible(true)
  }

  const runVisibleAnimation = (visible: boolean) => {
    animation.set(
      withTiming(
        visible ? 100 : 0,
        modalTimingConfig,
        !visible
          ? () => {
              'worklet'
              scheduleOnRN(unMountContent)
            }
          : () => {
              'worklet'
              scheduleOnRN(show)
            }
      )
    )
    isClosing.set(!visible)
  }

  useEffect(() => {
    // Don't run animations on mount if not enabled
    if (!wasVisible.current && !enabled) {
      return
    }
    wasVisible.current = true
    runVisibleAnimation(enabled)
  }, [enabled])

  // Trigger close animation
  const close = () => {
    const canClose = beforeClose ? beforeClose() : true
    if (!canClose) {
      return
    }
    runVisibleAnimation(false)
  }

  const ctx = {
    close,
    animation,
    isClosing,
  }

  const id = useId()

  const render = enabled || visible

  if (!render && !persistent) {
    return null
  }

  const onPress = backdropClose !== false ? close : undefined

  const childrenArr = onPress
    ? Children.toArray(children).map((child) => {
        // If the child already has a close prop, we need to pass the onPress function
        // This is same close but wrapped with animation
        if (
          isValidElement<{ close?: () => void }>(child) &&
          !!child.props.close
        ) {
          return cloneElement(child, { close: onPress })
        }
        return child
      })
    : children

  return (
    <>
      <Portal hostName={hostName}>
        <View
          key={id}
          className={cn(
            'absolute h-screen w-screen flex-1',
            !enabled && 'pointer-events-none'
          )}
        >
          <Suspense>
            <ModalContext.Provider value={ctx}>
              <ModalContent
                enabled={enabled}
                backdropContent={backdropContent}
                contentClassName={contentClassName}
                animation={animation}
                extraGestures={extraGestures}
                onPress={onPress}
                intensity={intensity}
              >
                {childrenArr}
              </ModalContent>
            </ModalContext.Provider>
          </Suspense>
        </View>
      </Portal>
    </>
  )
}
